/**
 * Zod schemas for everything under `content/`.
 *
 * `content/README.md` documents the shape of each file and is the contract
 * these schemas are written against; the content session owns the files and
 * this package never writes them. Parsing is strict about structure and silent
 * about prose: a schema says a statement template must name its slots, never
 * what the statement may say.
 *
 * Ids are stable and stored in the database (fragments held, relics won, code
 * names inside a frozen letter, letter bodies). They are never renumbered, so
 * every schema below treats an id as an opaque non-empty string rather than
 * enforcing a numbering scheme that content would then be bound to.
 */

import { z } from 'zod'
import { REPUTATION_TIERS } from '../game-config.ts'

/** A locale tag as it appears as a directory name under `content/`. */
export type Locale = string

const id = z.string().min(1)
const text = z.string().min(1)
const tier = z.union([z.literal(1), z.literal(2), z.literal(3)])

// --- locales.json ----------------------------------------------------------

export const localeInfoSchema = z.object({
  name: text,
  /** Offered to players. Only `en` is served until release (Q12.5). */
  served: z.boolean(),
  /** False keeps English island names as proper nouns in this locale (Q12.4). */
  translateIslandNames: z.boolean(),
})
export type LocaleInfo = z.infer<typeof localeInfoSchema>

export const localesFileSchema = z.object({
  source: id,
  locales: z.record(id, localeInfoSchema),
})
export type LocalesFile = z.infer<typeof localesFileSchema>

// --- fragments.json --------------------------------------------------------

export const fragmentSchema = z.object({
  id,
  tier,
  /** Kebab-case voice id, e.g. `ships-log`. Never translated, never shown. */
  voice: id,
  text,
})
export type Fragment = z.infer<typeof fragmentSchema>

export const fragmentsFileSchema = z.object({ fragments: z.array(fragmentSchema).min(1) })
export type FragmentsFile = z.infer<typeof fragmentsFileSchema>

// --- island-names.json -----------------------------------------------------

/**
 * The lists a name pattern draws from. `noun` carries both numbers because
 * `{noun}` reads the singular and `{nounPlural}` the plural of the same entry,
 * so both renderings of one generated name stay in step across locales.
 */
export const islandNameListsSchema = z.object({
  adjective: z.array(text).min(1),
  noun: z.array(z.object({ singular: text, plural: text })).min(1),
  saint: z.array(text).min(1),
  eventPhrase: z.array(text).min(1),
  compoundFirst: z.array(text).min(1),
  compoundSecond: z.array(text).min(1),
  numberWord: z.array(text).min(1),
})
export type IslandNameLists = z.infer<typeof islandNameListsSchema>

/** The list a `{slot}` in an island-name template reads from. */
export const ISLAND_NAME_SLOTS = [
  'adjective',
  'noun',
  'nounPlural',
  'saint',
  'eventPhrase',
  'compoundFirst',
  'compoundSecond',
  'numberWord',
] as const
export type IslandNameSlot = (typeof ISLAND_NAME_SLOTS)[number]

export const islandNamePatternSchema = z.object({
  id,
  template: text,
  /** Relative draw weight; identical in every locale. */
  weight: z.number().positive(),
})
export type IslandNamePattern = z.infer<typeof islandNamePatternSchema>

export const islandNamesFileSchema = z.object({
  patterns: z.array(islandNamePatternSchema).min(1),
  lists: islandNameListsSchema,
  /** Fixture names for the tests; each must be producible by some pattern. */
  examples: z.array(text),
})
export type IslandNamesFile = z.infer<typeof islandNamesFileSchema>

// --- log-lines.json --------------------------------------------------------

/**
 * Every event the server may report. A move response carries `{ event, seed }`
 * and never a sentence (Q12.2), so this list is an API contract as much as a
 * content one: adding an event means adding lines in every locale that has any.
 */
export const LOG_EVENTS = [
  'step-sand',
  'step-grass',
  'step-forest',
  'step-mountain',
  'water-edge',
  'ruin-found',
  'relic-found',
  'relic-spoken-for',
  'stamina-exhausted',
  'island-arrived',
  'return-landing',
  'island-sinking',
  'hearsay-charted',
  'hearsay-false',
  'relic-won',
  'relic-lost',
] as const
export type LogEvent = (typeof LOG_EVENTS)[number]

export const logLinesFileSchema = z.object({
  events: z.record(z.enum(LOG_EVENTS), z.array(text).min(1)),
})
export type LogLinesFile = z.infer<typeof logLinesFileSchema>

// --- code-names.json -------------------------------------------------------

/** The mood buckets a code name is drawn from (Q4.5). */
export const MOODS = ['benevolent', 'wry', 'wary', 'ceremonial', 'ominous'] as const
export type Mood = (typeof MOODS)[number]

export const codeNameSchema = z.object({ id, mood: z.enum(MOODS), text })
export type CodeName = z.infer<typeof codeNameSchema>

export const codeNamesFileSchema = z.object({
  moods: z.array(z.enum(MOODS)).min(1),
  /**
   * Mood weights per reputation tier. Each row sums to 1 and every cell is at
   * least the floor, so every mood is reachable from every tier and a name
   * cannot be inverted into a score (`docs/anonymity.md` R7, L2).
   */
  tierWeights: z.record(z.enum(REPUTATION_TIERS), z.record(z.enum(MOODS), z.number().min(0))),
  names: z.array(codeNameSchema).min(1),
})
export type CodeNamesFile = z.infer<typeof codeNamesFileSchema>

// --- letter-grammar.json ---------------------------------------------------

/**
 * Slot types with a value list in the grammar. `coord` is a built-in with no
 * list: its value is `{ x, y }` on the island grid, rendered `x, y`.
 */
export const SLOT_TYPES = [
  'direction',
  'feature',
  'terrain',
  'distance',
  'landmark',
  'count',
  'judgement',
  'timeOfDay',
] as const
export type SlotType = (typeof SLOT_TYPES)[number]

/** The built-in slot type, which has no list and is validated against the grid. */
export const COORD_SLOT = 'coord' as const
export type SlotTypeOrCoord = SlotType | typeof COORD_SLOT

export const phraseSchema = z.object({ id, text })
export type Phrase = z.infer<typeof phraseSchema>

export const slotValueSchema = z.object({
  id,
  text,
  /**
   * Optional grammatical forms, referenced from a template as `{name.form}`
   * (Q12.3). English uses none; a locale that inflects supplies its own.
   */
  forms: z.record(id, text).optional(),
})
export type SlotValue = z.infer<typeof slotValueSchema>

export const statementSchema = z.object({
  id,
  category: id,
  template: text,
  /** Slot name in the template to its type; identical in every locale. */
  slots: z.record(id, z.enum([...SLOT_TYPES, COORD_SLOT])),
})
export type Statement = z.infer<typeof statementSchema>

/** A coordinate as a letter carries it. Bounds are checked against the grid. */
export const coordValueSchema = z.object({ x: z.number().int(), y: z.number().int() })
export type CoordValue = z.infer<typeof coordValueSchema>

/** A filled slot: a value id from its own list, or a coordinate. */
export const filledSlotSchema = z.union([id, coordValueSchema])

export const presetSchema = z.object({
  id,
  label: text,
  opening: id,
  statements: z.array(z.object({ statement: id, slots: z.record(id, filledSlotSchema) })).min(1),
  closing: id,
})
export type Preset = z.infer<typeof presetSchema>

export const letterGrammarFileSchema = z.object({
  openings: z.array(phraseSchema).min(1),
  closings: z.array(phraseSchema).min(1),
  slots: z.record(z.enum(SLOT_TYPES), z.array(slotValueSchema).min(1)),
  categories: z.array(z.object({ id, label: text })).min(1),
  statements: z.array(statementSchema).min(1),
  presets: z.array(presetSchema),
})
export type LetterGrammarFile = z.infer<typeof letterGrammarFileSchema>

// --- relics.json -----------------------------------------------------------

export const relicSchema = z.object({ id, name: text, description: text, tier })
export type Relic = z.infer<typeof relicSchema>

export const relicsFileSchema = z.object({ relics: z.array(relicSchema).min(1) })
export type RelicsFile = z.infer<typeof relicsFileSchema>

// --- guild-voice.json ------------------------------------------------------

export const guildVoiceFileSchema = z.object({
  welcome: text,
  risen: z.array(text).min(1),
  sinking: z.array(text).min(1),
  codeOfConduct: text,
  circulars: z.object({ guildComplete: text }),
  /** Every other short player-facing string, keyed by purpose. ICU. */
  ui: z.record(id, z.string()),
})
export type GuildVoiceFile = z.infer<typeof guildVoiceFileSchema>

// --- manuscript.json -------------------------------------------------------

export const manuscriptSpanSchema = z.object({
  id,
  order: z.number().int().positive(),
  /** Legible once the player holds this many tier-3 fragments (Q6.4). */
  unlockAtTier3Found: z.number().int().nonnegative(),
  text,
})
export type ManuscriptSpan = z.infer<typeof manuscriptSpanSchema>

export const manuscriptFileSchema = z.object({
  title: text,
  spans: z.array(manuscriptSpanSchema).min(1),
})
export type ManuscriptFile = z.infer<typeof manuscriptFileSchema>

// --- the locale bundle -----------------------------------------------------

/** Every file a locale directory may hold, by base name. */
export const CONTENT_FILES = [
  'fragments',
  'island-names',
  'log-lines',
  'code-names',
  'letter-grammar',
  'relics',
  'guild-voice',
  'manuscript',
] as const
export type ContentFileName = (typeof CONTENT_FILES)[number]

export const contentFileSchemas = {
  fragments: fragmentsFileSchema,
  'island-names': islandNamesFileSchema,
  'log-lines': logLinesFileSchema,
  'code-names': codeNamesFileSchema,
  'letter-grammar': letterGrammarFileSchema,
  relics: relicsFileSchema,
  'guild-voice': guildVoiceFileSchema,
  manuscript: manuscriptFileSchema,
} as const satisfies Record<ContentFileName, z.ZodType>

export type LocaleContent = {
  fragments: FragmentsFile
  'island-names': IslandNamesFile
  'log-lines': LogLinesFile
  'code-names': CodeNamesFile
  'letter-grammar': LetterGrammarFile
  relics: RelicsFile
  'guild-voice': GuildVoiceFile
  manuscript: ManuscriptFile
}

/**
 * A locale may be partial. Any missing file, entry or key falls back to the
 * source locale rather than failing (Q12.5), so a half-finished translation is
 * playable rather than broken.
 */
export type PartialLocaleContent = Partial<LocaleContent>

/** Everything under `content/`, already read from wherever it lives. */
export type ContentSource = {
  locales: LocalesFile
  byLocale: Record<Locale, PartialLocaleContent>
}

/** Parse one locale's raw JSON. Throws naming the file and the locale. */
export function parseLocaleContent(
  raw: Partial<Record<ContentFileName, unknown>>,
  locale: Locale,
): PartialLocaleContent {
  const parsed: PartialLocaleContent = {}
  for (const name of CONTENT_FILES) {
    const value = raw[name]
    if (value === undefined) continue
    const result = contentFileSchemas[name].safeParse(value)
    if (!result.success) {
      throw new Error(
        `content/${locale}/${name}.json does not match its schema: ${result.error.message}`,
      )
    }
    // Each schema returns the type its key names; the map above pins that.
    Object.assign(parsed, { [name]: result.data })
  }
  return parsed
}

/** Parse `locales.json` and adopt already-parsed locale bundles. */
export function parseContentSource(input: {
  locales: unknown
  byLocale: Record<Locale, PartialLocaleContent>
}): ContentSource {
  const result = localesFileSchema.safeParse(input.locales)
  if (!result.success) {
    throw new Error(`content/locales.json does not match its schema: ${result.error.message}`)
  }
  return { locales: result.data, byLocale: input.byLocale }
}
